import { useCallback, useSyncExternalStore } from 'react';
import type { Ebene } from './ebenen';
import { nullpunktAusGeschoss, type Nullpunkt } from './geschoss';

/**
 * Verortungs-Sitzung einer Begehung (docs/verortung-kamera-vertrag.md, Paket A).
 *
 * Hält den Nullpunkt (an bekannter Ebene gemessener Druck), die zuletzt
 * bestätigte Ebene (Hysterese) und ob gerade Gerüstlagen statt Geschosse
 * geschätzt werden. Nur im sessionStorage des Geräts — nie am Server, nie in
 * localStorage: eine Begehung endet mit dem Tab. Ein Nullpunkt älter als 4 h
 * taugt wegen der Wetterdrift nicht mehr und wird beim Laden verworfen.
 */

/** Ablage wie in src/services/sensorMessprotokoll.ts (sessionStorage/localStorage oder Testattrappe). */
export interface Speicher {
    getItem(k: string): string | null;
    setItem(k: string, v: string): void;
    removeItem(k: string): void;
}

export interface VerortungsSitzung {
    nullpunkt: Nullpunkt | null;
    vorherigesId: string | null;
    geruestModus: boolean;
}

export const LEERE_SITZUNG: VerortungsSitzung = Object.freeze({ nullpunkt: null, vorherigesId: null, geruestModus: false });

/** 4 h: ein älterer Nullpunkt wird verworfen (Drift 2,5 m/h ⇒ 10 m, Sicherheit längst 0). */
export const SITZUNG_MAX_ALTER_MS = 4 * 3_600_000;

const SCHLUESSEL_PRAEFIX = 'sigeko.verortungSitzung.';

const standardSpeicher = (): Speicher | null => {
    try {
        return typeof sessionStorage !== 'undefined' ? sessionStorage : null;
    } catch {
        return null;
    }
};

function istObjekt(x: unknown): x is Record<string, unknown> {
    return !!x && typeof x === 'object' && !Array.isArray(x);
}

function endlich(x: unknown): x is number {
    return typeof x === 'number' && Number.isFinite(x);
}

/**
 * Nullpunkt verwerfen, wenn er mehr als 4 h vom Jetzt entfernt liegt — auch in
 * der Zukunft (verstellte Uhr) — oder das Jetzt selbst ungültig ist.
 */
function nullpunktAbgelaufen(n: Nullpunkt | null, jetzt: number): boolean {
    if (!n) return false;
    if (!Number.isFinite(jetzt)) return true;
    return Math.abs(jetzt - n.t) > SITZUNG_MAX_ALTER_MS;
}

function nullpunktLesen(roh: unknown): Nullpunkt | null {
    if (!istObjekt(roh)) return null;
    const { hoeheM, druckHpa, t } = roh;
    if (!endlich(hoeheM) || !endlich(druckHpa) || druckHpa <= 0 || !endlich(t)) return null;
    return { hoeheM, druckHpa, t };
}

/** Kaputtes → LEERE_SITZUNG; abgelaufener Nullpunkt → ohne Nullpunkt (und ohne Hysterese-Ebene). */
function sitzungAusText(text: string | null, jetzt: number): VerortungsSitzung {
    if (text === null) return LEERE_SITZUNG;
    let roh: unknown;
    try {
        roh = JSON.parse(text);
    } catch {
        return LEERE_SITZUNG;
    }
    if (!istObjekt(roh)) return LEERE_SITZUNG;
    let nullpunkt = nullpunktLesen(roh.nullpunkt);
    let vorherigesId = typeof roh.vorherigesId === 'string' && roh.vorherigesId !== '' ? roh.vorherigesId : null;
    if (nullpunktAbgelaufen(nullpunkt, jetzt)) {
        nullpunkt = null;
        vorherigesId = null;
    }
    return { nullpunkt, vorherigesId, geruestModus: roh.geruestModus === true };
}

/**
 * Sitzung aus dem sessionStorage (`sigeko.verortungSitzung.<schluessel>`).
 * Fehlt, kaputt oder kein Speicher → LEERE_SITZUNG; Nullpunkt älter als
 * SITZUNG_MAX_ALTER_MS → verworfen. Nichts wirft.
 */
export function sitzungLaden(schluessel: string, jetzt: number, speicher: Speicher | null = standardSpeicher()): VerortungsSitzung {
    if (!speicher) return LEERE_SITZUNG;
    try {
        return sitzungAusText(speicher.getItem(SCHLUESSEL_PRAEFIX + schluessel), jetzt);
    } catch {
        return LEERE_SITZUNG;
    }
}

/** Speichern; leere Sitzung entfernt den Schlüssel. Voller/fehlender Speicher ist kein Fehler. */
export function sitzungSpeichern(schluessel: string, s: VerortungsSitzung, speicher: Speicher | null = standardSpeicher()): void {
    if (!speicher) return;
    try {
        const k = SCHLUESSEL_PRAEFIX + schluessel;
        if (!s || (!s.nullpunkt && !s.vorherigesId && !s.geruestModus)) {
            speicher.removeItem(k);
            return;
        }
        speicher.setItem(k, JSON.stringify({ nullpunkt: s.nullpunkt, vorherigesId: s.vorherigesId, geruestModus: s.geruestModus }));
    } catch {
        // Nur Komfort: ohne Ablage gilt der Nullpunkt bis zum Neuladen der Seite.
    }
}

/**
 * „Hier bin ich“: bestätigte Ebene ⇒ neuer Nullpunkt (Höhe der Ebene, Druck
 * zum Zeitpunkt t), vorherigesId = Ebene, geruestModus = art === 'geruest'.
 * Ohne gültigen Druck (≤ 0, nicht endlich) oder Zeitpunkt bleibt der alte
 * Nullpunkt — die Ebene gilt trotzdem als bestätigt.
 */
export function ebeneBestaetigen(s: VerortungsSitzung, ebene: Ebene, druckHpa: number, t: number): VerortungsSitzung {
    const messbar = Number.isFinite(druckHpa) && druckHpa > 0 && Number.isFinite(t);
    return {
        nullpunkt: messbar ? nullpunktAusGeschoss(ebene, druckHpa, t) : (s?.nullpunkt ?? null),
        vorherigesId: ebene.id,
        geruestModus: ebene.art === 'geruest',
    };
}

// ─── React-Hook ──────────────────────────────────────────────────────────────
// Stand je Schlüssel im Speicher der Seite; der sessionStorage ist nur die
// Ablage für ein Neuladen. So sehen alle Nutzer desselben Schlüssels (Kamera,
// Vorschlagskarte im Wizard) sofort dieselbe Sitzung — auch wenn die Ablage
// voll oder gesperrt ist.

const sitzungen = new Map<string, VerortungsSitzung>();
const abonnenten = new Map<string, Set<() => void>>();

function sitzungSchnappschuss(schluessel: string): VerortungsSitzung {
    const jetzt = Date.now();
    const bekannt = sitzungen.get(schluessel);
    if (bekannt && !nullpunktAbgelaufen(bekannt.nullpunkt, jetzt)) return bekannt;
    const wert = bekannt ? { ...bekannt, nullpunkt: null, vorherigesId: null } : sitzungLaden(schluessel, jetzt);
    sitzungen.set(schluessel, wert);
    return wert;
}

function sitzungAbonnieren(schluessel: string, rueckruf: () => void): () => void {
    const liste = abonnenten.get(schluessel) ?? new Set<() => void>();
    abonnenten.set(schluessel, liste);
    liste.add(rueckruf);
    return () => {
        liste.delete(rueckruf);
        if (liste.size === 0 && abonnenten.get(schluessel) === liste) abonnenten.delete(schluessel);
    };
}

function sitzungSetzen(schluessel: string, s: VerortungsSitzung): void {
    sitzungen.set(schluessel, s);
    sitzungSpeichern(schluessel, s);
    for (const r of [...(abonnenten.get(schluessel) ?? [])]) r();
}

/**
 * Sitzung zum Schlüssel (z. B. Projekt-/Berichts-id) samt Setter. Der Setter
 * speichert sofort (sessionStorage 'sigeko.verortungSitzung.<schluessel>') und
 * meldet die Änderung allen Hook-Nutzern desselben Schlüssels.
 */
export function useVerortungsSitzung(schluessel: string): [VerortungsSitzung, (s: VerortungsSitzung) => void] {
    const abonnieren = useCallback((r: () => void) => sitzungAbonnieren(schluessel, r), [schluessel]);
    const lesen = useCallback(() => sitzungSchnappschuss(schluessel), [schluessel]);
    const sitzung = useSyncExternalStore(abonnieren, lesen, lesen);
    const setzen = useCallback((s: VerortungsSitzung) => sitzungSetzen(schluessel, s), [schluessel]);
    return [sitzung, setzen];
}
